/**
 * SÂN ĐẤU — nền, Nhà (core), cổng quái.
 * Vẽ 1 lần bằng Graphics/Canvas nên gần như không tốn hiệu năng.
 *
 * ẢNH AI: sân có thể được thay bằng ảnh thật (nền trời, sàn đá, Nhà, decor) do
 * `loadBattlefieldArt()` nạp. Ảnh về SAU khi sân đã dựng xong ⇒ mọi thứ thay thế được
 * tại chỗ (`applyArt`), và nếu ảnh thiếu thì bản vẽ code dưới đây vẫn là mặc định.
 * Xem `battlefield-art.ts` để biết vì sao tách riêng đường ống art.
 */
import Phaser from 'phaser';
import { t } from '../i18n';
import { ARENA } from '../sim/world';
import type { Core } from '../sim/types';
import { loadBattlefieldArt, coverCrop, type BattlefieldArt } from './battlefield-art';

const SKY_KEY = '__tdk_sky';
const FLOOR_KEY = '__tdk_floor';
const SKY_FADE_KEY = '__tdk_sky_fade';

function gradTexture(scene: Phaser.Scene, key: string, w: number, h: number, stops: Array<[number, string]>, vertical = true): string {
  if (scene.textures.exists(key)) return key;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const g = vertical ? ctx.createLinearGradient(0, 0, 0, h) : ctx.createLinearGradient(0, 0, w, 0);
  for (const [p, col] of stops) g.addColorStop(p, col);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  scene.textures.addCanvas(key, c);
  return key;
}

/**
 * Texture MỜ DẦN từ trong suốt → một màu đặc (dọc).
 * Dùng để nối mép dưới dải trời vào sàn: cắt cứng giữa "cảnh xa" và "sân đá" trông
 * như dán giấy, còn một dải mờ 36px thì mắt đọc ra chiều sâu.
 */
function fadeTexture(scene: Phaser.Scene, key: string, color: string, w = 8, h = 64): string {
  if (scene.textures.exists(key)) return key;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, color);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  scene.textures.addCanvas(key, c);
  return key;
}

function radialTexture(scene: Phaser.Scene, key: string, size: number, inner: string, outer: string): string {
  if (scene.textures.exists(key)) return key;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  scene.textures.addCanvas(key, c);
  return key;
}

/** Ảnh "tia lửa" 4 cánh — dùng cho hiệu ứng va chạm/chiêu. */
function sparkTexture(scene: Phaser.Scene, key: string, size = 128): string {
  if (scene.textures.exists(key)) return key;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  const cx = size / 2;
  const g = ctx.createRadialGradient(cx, cx, 0, cx, cx, cx);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  // 4 tia
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.beginPath();
  ctx.ellipse(cx, cx, size * 0.46, size * 0.05, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx, cx, size * 0.05, size * 0.46, 0, 0, Math.PI * 2);
  ctx.fill();
  scene.textures.addCanvas(key, c);
  return key;
}

export const SPARK_KEY = '__tdk_spark';
export const GLOW_KEY = '__tdk_glow';
export const SHADOW_KEY = '__tdk_shadow';

/**
 * CHỖ ĐẶT DECOR khi có ảnh — chọn TAY để không bao giờ đè lên:
 *   · vùng trung tâm (Nhà + bán kính ~230px quanh core, nơi tướng đứng và đánh nhau)
 *   · 4 CỔNG QUÁI ở giữa 4 cạnh (quái đi vào từ đó — che cổng là lỗi chơi game)
 *   · mép trên/dưới: `y` đã tính sao cho đỉnh prop không vượt quá y=0 (bị camera cắt cụt)
 *
 * `prop` = chỉ số ô trong lưới `arena-decor.png` (xem `tools/gen-art-2.sh` §3):
 *   0 bụi tre · 1 đèn lồng · 2 sư tử đá · 3 băng rôn · 4 thùng gỗ
 *   5 cây cổ thụ · 6 chuông đồng · 7 sạp chợ · 8 đèn đá
 * `h` = CHIỀU CAO mong muốn (px). Không prop nào quá ~140px ⇒ không át nhân vật.
 * Mỗi prop dùng đúng 1 lần, rải khắp 4 góc + 4 mép ⇒ sân có chiều sâu mà vẫn thoáng.
 */
const DECOR_SLOTS: Array<{ x: number; y: number; prop: number; h: number; flip?: boolean }> = [
  { x: 96, y: 140, prop: 5, h: 135 }, // góc trên-trái: cây cổ thụ (to nhất, neo góc)
  { x: 906, y: 132, prop: 0, h: 130, flip: true }, // góc trên-phải: bụi tre
  { x: 74, y: 152, prop: 1, h: 72 }, // mép trái, TRÊN cổng trái: đèn lồng
  { x: 928, y: 158, prop: 3, h: 118, flip: true }, // mép phải, TRÊN cổng phải: băng rôn
  { x: 76, y: 440, prop: 4, h: 60 }, // mép trái, DƯỚI cổng trái: thùng gỗ
  { x: 900, y: 470, prop: 7, h: 100, flip: true }, // mép phải, DƯỚI cổng phải: sạp chợ
  { x: 214, y: 558, prop: 8, h: 84 }, // mép dưới-trái (tránh cổng dưới ở x≈500): đèn đá
  { x: 786, y: 554, prop: 6, h: 94, flip: true }, // mép dưới-phải: chuông đồng
  { x: 334, y: 182, prop: 2, h: 78 }, // mép trên (tránh cổng trên ở x≈500): sư tử đá —
  // đẩy xuống dưới dải trời (SKY_H): nếu để y≈118 thì prop nằm TRỌN trong dải trời
  // và trông như lơ lửng giữa cảnh làng quê xa.
];

/** Bề ngang tối đa cho 1 prop — chặn prop bè ngang tràn vào sân. */
const DECOR_MAX_W = 120;

/**
 * DẢI TRỜI ở mép trên (px).
 *
 * Vì sao cần: `arena-bg.png` là cảnh làng quê ban đêm (mặt trăng · chùa · đèn lồng ·
 * tre) nhưng sàn lại phủ trọn 1000×600 ⇒ nền bị che 95%, sinh ảnh ra mà không ai thấy.
 * Chừa 132px phía trên cho nền làm nền "xa", phần còn lại vẫn là sân đá.
 *
 * ⚠️ VÙNG CHƠI CỦA `sim/` KHÔNG ĐỔI (vẫn 1000×600) — đây thuần là chuyện hiển thị.
 * Quái vẫn đi qua dải trên; chúng được vẽ ĐÈ lên nền (depth theo `y`) nên đọc ra
 * "đi trước cảnh xa", không phải lỗi.
 */
const SKY_H = 132;

/**
 * DỊCH DẢI CẮT ẢNH NỀN (0 = sát mép trên · 0,5 = giữa ảnh · 1 = sát mép dưới).
 *
 * Vì sao cần: khung trời chỉ cao 132px ⇒ chỉ thấy ~31% chiều cao ảnh. Dải giữa TUYỆT ĐỐI của ảnh
 * võ lâm (2048×869) cắt mất MẶT TRĂNG (nằm ở khoảng y 95..300). Đã đo bằng ảnh so sánh 4 mức
 * (`art/verify/arena-band-so-sanh.png`):
 *   0,18 → trăng trọn nhưng KHÔNG có đền (chỉ hở mái) · 0,25 → trăng + đền sáng đèn + cổng sơn môn ✔
 *   0,34 → trăng bị cắt gần hết · 0,45 → mất trăng
 * Số này là thứ DUY NHẤT phải chỉnh khi thay ảnh nền mới — xem `art/ai/prompt-arena-bg.txt`.
 */
const SKY_BAND_BIAS = 0.25;

export class ArenaView {
  private decor: Phaser.GameObjects.GameObject[] = [];
  private coreGfx: Phaser.GameObjects.Graphics;
  private coreBarBg: Phaser.GameObjects.Rectangle;
  private coreBar: Phaser.GameObjects.Rectangle;
  private coreShieldBar: Phaser.GameObjects.Rectangle;
  private coreLabel: Phaser.GameObjects.Text;
  private glow: Phaser.GameObjects.Image;

  /** ảnh AI đã nạp được (null = chưa có/không có ⇒ dùng bản vẽ code) */
  private art: BattlefieldArt | null = null;
  private skyImg: Phaser.GameObjects.Image;
  private floorImg: Phaser.GameObjects.Image;
  /**
   * Ảnh SÀN AI — vẽ **MỘT LẦN**, KHÔNG lặp.
   *
   * ⚠️⚠️ ĐỔI TỪ `TileSprite` SANG `Image` — LÝ DO LÀ MỘT LỖI THỊ GIÁC THẬT:
   * bản cũ dùng `tileSprite` + `tileScale = max(cover)` để phủ kín vùng sàn. Với ảnh gạch LIỀN MẠCH
   * (`arena-floor.png`) cách đó đúng, nhưng với ảnh BẢN ĐỒ có sông + 2 cầu (`arena-river.png` dùng ở
   * Arena PVE) thì nó LẶP cả bức bản đồ ⇒ sông/ cầu xuất hiện nhiều lần trên sân, trông như lỗi.
   * `Image` + crop đúng tỉ lệ khung chỉ hiện ảnh MỘT lần, và vì tỉ lệ ảnh ≈ tỉ lệ khung nên phần bị
   * cắt gần như bằng 0 ⇒ cỡ phiến đá trên sân KHÔNG đổi so với bản `tileSprite` (điều kiện bắt buộc:
   * phiến đá phải nhỏ hơn nhân vật — xem chú thích dài ở `applyArt`).
   */
  private floorArt: Phaser.GameObjects.Image | null = null;
  private houseImg: Phaser.GameObjects.Image | null = null;
  /** trạng thái Nhà đang vẽ (0/1/2) — chỉ để biết khi nào cần tính lại scale */
  private houseState = -1;
  /** tint đang áp cho ảnh Nhà — tránh setTint lại mỗi frame (drawCore chạy 60 lần/giây) */
  private houseTint = -1;
  private decorImgs: Phaser.GameObjects.Image[] = [];
  /** lưới vân sàn + viền + vòng tròn + cổng — vẽ lại được khi có dải trời */
  private floorGfx: Phaser.GameObjects.Graphics;
  /** dải mờ nối dải trời vào sàn (chỉ có khi nền AI đã nạp) */
  private skyFade: Phaser.GameObjects.Image | null = null;
  /** % máu gần nhất — ảnh Nhà về muộn thì vẽ lại đúng trạng thái hiện tại */
  private lastPct = 1;
  /** scene đã shutdown chưa (ảnh nạp xong sau khi rời sân ⇒ đừng đụng GameObject đã chết) */
  private disposed = false;

  constructor(private scene: Phaser.Scene) {
    // BẮT BUỘC tạo trước: nếu thiếu, Phaser vẽ texture "missing" (ô xanh có đường chéo)
    sparkTexture(scene, SPARK_KEY);
    radialTexture(scene, GLOW_KEY, 128, 'rgba(255,214,140,0.55)', 'rgba(255,214,140,0)');
    radialTexture(scene, SHADOW_KEY, 128, 'rgba(0,0,0,0.55)', 'rgba(0,0,0,0)');

    // Nhà có thể dựng lại khi người chơi vào lại sân ⇒ đừng để callback ảnh
    // chọc vào GameObject đã bị Phaser huỷ.
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.disposed = true;
    });

    // bầu trời — depth -100 (dưới cùng). Khi có ảnh AI sẽ `setTexture(art.bg)` tại chỗ.
    this.skyImg = scene.add
      .image(ARENA.w / 2, ARENA.h / 2, gradTexture(scene, SKY_KEY, 8, 256, [
        [0, '#2b1d3a'],
        [0.45, '#4a2c3d'],
        [1, '#7a4a3a'],
      ]))
      .setDisplaySize(ARENA.w, ARENA.h)
      .setDepth(-100);

    // sàn đấu — depth -90, nằm TRÊN nền trời và DƯỚI lưới vân (-80).
    // Ảnh AI sẽ thay bằng TileSprite (texture đá tile được) chứ không kéo giãn.
    this.floorImg = scene.add
      .image(ARENA.w / 2, ARENA.h / 2, gradTexture(scene, FLOOR_KEY, 8, 256, [
        [0, '#6b4a33'],
        [0.6, '#573b28'],
        [1, '#3d2a1d'],
      ]))
      .setDisplaySize(ARENA.w, ARENA.h)
      .setDepth(-90)
      .setAlpha(0.92);

    this.floorGfx = scene.add.graphics().setDepth(-80);
    // mặc định: sàn phủ TRỌN sân (chưa có nền AI). Có nền AI thì `applyArt` gọi lại
    // với `floorTop = SKY_H` để chừa dải trời ở trên.
    this.drawFloorGfx(0, false);

    this.drawDecor();

    // Nhà
    this.glow = scene.add
      .image(ARENA.coreX, ARENA.coreY, radialTexture(scene, GLOW_KEY, 128, 'rgba(255,214,140,0.55)', 'rgba(255,214,140,0)'))
      .setDisplaySize(190, 190)
      .setDepth(-5)
      .setBlendMode(Phaser.BlendModes.ADD);

    this.coreGfx = scene.add.graphics().setDepth(-4);
    this.drawCore(1);

    // thanh máu Nhà
    const bw = 220;
    this.coreBarBg = scene.add.rectangle(ARENA.coreX, ARENA.coreY + 74, bw, 13, 0x1c1917, 0.85).setDepth(60).setStrokeStyle(2, 0x2a1f1a);
    this.coreShieldBar = scene.add.rectangle(ARENA.coreX - bw / 2, ARENA.coreY + 74, 0, 9, 0x38bdf8).setOrigin(0, 0.5).setDepth(61);
    this.coreBar = scene.add.rectangle(ARENA.coreX - bw / 2 + 2, ARENA.coreY + 74, bw - 4, 9, 0x22c55e).setOrigin(0, 0.5).setDepth(62);
    this.coreLabel = scene.add
      .text(ARENA.coreX, ARENA.coreY + 90, t('NHÀ', 'HOUSE'), { fontFamily: 'system-ui, sans-serif', fontSize: '12px', color: '#fde68a' })
      .setOrigin(0.5, 0)
      .setDepth(62);
    void this.coreLabel;

    /*
     * NẠP ẢNH AI — KHÔNG await, cố tình.
     * Mở màn phải hiện sân ngay; ảnh có thể tới muộn (agy còn đang sinh) nên sân
     * chạy bằng bản vẽ code trước, ảnh về thì `applyArt` thay thế tại chỗ.
     * `loadBattlefieldArt` không bao giờ reject ⇒ không cần `.catch`, nhưng thêm cho chắc:
     * một promise lỗi mà không ai bắt sẽ làm cả trang trắng.
     */
    void loadBattlefieldArt(scene)
      .then((art) => {
        if (!this.disposed) this.applyArt(art);
      })
      .catch((e) => console.warn('[arena] bỏ qua art sân đấu:', e));
  }

  /**
   * THAY BẢN VẼ CODE BẰNG ẢNH AI (chỉ thay phần nào có ảnh).
   *
   * Depth giữ NGUYÊN như bản vẽ code để thứ tự lớp không đổi:
   *   -100 nền trời · -90 sàn · -80 lưới vân/viền/vòng tròn/cổng · -70..-60 decor
   *   -5 quầng sáng Nhà · -4 Graphics Nhà (bệ đá) · -3 ảnh Nhà · 60..62 thanh máu
   * Ảnh Nhà CỐ TÌNH nằm dưới thanh máu (60) — thanh máu là thứ người chơi phải
   * đọc được trong lúc Nhà đang cháy.
   */
  /** ĐỔI NGÔN NGỮ: nhãn dưới Nhà (vẽ bằng Phaser Text, không phải DOM nên phải set tay). */
  refreshLang(): void {
    this.coreLabel.setText(t('NHÀ', 'HOUSE'));
  }

  private applyArt(art: BattlefieldArt): void {
    this.art = art;
    /*
     * CÓ NỀN AI ⇒ CHỪA DẢI TRỜI.
     * Nếu không chừa, sàn (đục) ở depth -90 che mất ảnh nền ở -100 ⇒ sinh ảnh làng quê
     * ban đêm mà người chơi không thấy gì (đúng lỗi đã gặp). Ảnh nền được CẮT đúng dải
     * có làng + đèn lồng (y 430..900 của ảnh gốc 1024) chứ không kéo cả ảnh.
     */
    const hasSky = !!art.bg;

    if (art.bg) {
      /*
       * CẮT MỘT DẢI NGANG **ĐÚNG TỈ LỆ KHUNG** Ở GIỮA ẢNH — không kéo méo.
       *
       * ⚠️ LỖI CŨ (đo được): code cắt cứng `setCrop(0, 60, 1536, 520)` (tỉ lệ 2,95:1) rồi
       * `setDisplaySize(1000, 132)` (tỉ lệ 7,58:1) ⇒ ảnh bị BÓP DỌC 2,6 lần. Đó là một phần
       * lý do ảnh nền trông "xấu/ sai" dù ảnh gốc ổn: núi non bị bẹp dúm lại.
       *
       * Cách đúng: lấy dải ngang có CHIỀU CAO = `width · SKY_H / ARENA.w` (đúng 7,58:1) rồi mới
       * đặt vào khung 1000×132 ⇒ tỉ lệ ảnh giữ nguyên 100%, không méo, và **không phụ thuộc kích
       * thước ảnh** (đổi ảnh nền sau này không phải sửa số nào).
       *
       * LỆCH TÂM (`SKY_BAND_BIAS`): dải giữa TUYỆT ĐỐI của ảnh võ lâm làm mất mặt trăng (nằm cao
       * hơn) ⇒ nhích dải lên trên một chút để có trăng + đền + cổng sơn môn trong cùng khung.
       */
      const src = this.scene.textures.get(art.bg).getSourceImage() as { width: number; height: number };
      const bandH = Math.max(1, Math.min(src.height, Math.round((src.width * SKY_H) / ARENA.w)));
      const y0 = Math.max(0, Math.min(src.height - bandH, Math.round((src.height - bandH) * SKY_BAND_BIAS)));
      this.skyImg
        .setTexture(art.bg)
        .setCrop(0, y0, src.width, bandH)
        .setDisplaySize(ARENA.w, SKY_H)
        .setPosition(ARENA.w / 2, SKY_H / 2);
      // dải mờ nối mép dưới dải trời vào sàn (tránh đường cắt cứng)
      this.skyFade = this.scene.add
        .image(ARENA.w / 2, SKY_H - 14, fadeTexture(this.scene, SKY_FADE_KEY, 'rgba(107,74,51,1)'))
        .setDisplaySize(ARENA.w, 28)
        .setDepth(-89);
    }

    // ---- SÀN: ảnh phủ KÍN vùng sàn, vẽ MỘT LẦN (crop cover) — không kéo méo, không lặp.
    // alpha 0.95: chừa 5% cho lớp gradient bên dưới "nhuộm" ấm lạnh theo màn,
    // đồng thời giữ lưới vân + vòng tròn trung tâm phía trên vẫn đọc rõ.
    const floorTop = hasSky ? SKY_H : 0;
    const floorH = ARENA.h - floorTop;
    if (art.floor && !this.floorArt) {
      this.floorImg.setVisible(false);
      /*
       * ⚠️ TỈ LỆ CHI TIẾT TRÊN SÀN — chỗ này từng sai và người dùng thấy ngay:
       * *"hình ảnh sàn đấu và chi tiết phải phù hợp với cả kích cỡ nhân vật và màn chơi"*.
       *
       * Trước đây ảnh sàn hiển thị **1:1** rồi lặp (`tileSprite` không đặt `tileScale`): ảnh cũ
       * 1024×1024 với phiến đá 127–186px ⇒ lên sân mỗi phiến **TO HƠN cả nhân vật** (cao 104 đơn vị,
       * xem `TARGET_CHAR_HEIGHT`), sân đọc ra như "cờ lớn" chứ không phải sân gạch. Ngoài ra sân chỉ
       * 1000×468 nên 1:1 nghĩa là **một nửa dưới ảnh không bao giờ hiện**.
       *
       * ⚠️⚠️ NAY ĐỔI TỪ `tileSprite` (LẶP) SANG `Image` (MỘT LẦN) — ĐỌC KỸ VÌ SAO:
       * bản trước dùng `tileSprite` + `tileScale = max(cover)` (`Math.max(ARENA.w / src.width,
       * floorH / src.height)`) để phủ kín vùng sàn. Với ảnh gạch LIỀN MẠCH (`arena-floor.png`) cách
       * đó đúng, NHƯNG với ảnh **BẢN ĐỒ** có sông + 2 cầu (`arena-river.png` — sân của Arena PVE)
       * thì nó LẶP CẢ BỨC BẢN ĐỒ ⇒ sông và 2 cầu hiện nhiều lần, trông như lỗi.
       * `coverCrop` cắt một hình chữ nhật ĐÚNG TỈ LỆ KHUNG ở giữa ảnh rồi `setDisplaySize` vào khung
       * ⇒ ảnh chỉ hiện MỘT lần, giữ đúng tỉ lệ (không bóp méo).
       *
       * 🔢 CỠ PHIẾN ĐÁ KHÔNG ĐỔI so với bản `tileSprite`: ảnh 2048×958 ≈ đúng tỉ lệ vùng sàn
       * (2,137:1) nên phần bị cắt ≈ 1px; tỉ lệ hiển thị vẫn 0,488 ⇒ phiến 56–101 đơn vị
       * ≈ 0,5–1,0 lần chiều cao nhân vật (đo 3 hàng, xem §Sàn đấu trong README). Muốn chi tiết
       * to/ nhỏ hơn thì sửa ẢNH (số phiến trong `art/ai/prompt-arena-floor.txt`), không sửa code.
       */
      const src = this.scene.textures.get(art.floor).getSourceImage() as { width: number; height: number };
      const crop = coverCrop(src.width, src.height, ARENA.w, floorH);
      this.floorArt = this.scene.add
        .image(ARENA.w / 2, floorTop + floorH / 2, art.floor)
        .setCrop(crop.x, crop.y, crop.width, crop.height)
        .setDisplaySize(ARENA.w, floorH)
        .setDepth(-90)
        .setAlpha(0.95);
    } else if (hasSky && !this.floorArt) {
      // có nền AI nhưng CHƯA có ảnh sàn ⇒ co cả sàn vẽ-code lại cho khớp dải trời
      this.floorImg.setPosition(ARENA.w / 2, floorTop + floorH / 2).setDisplaySize(ARENA.w, floorH);
    }

    // lưới vân + viền + vòng tròn + cổng: vẽ lại cho khớp vùng sàn mới.
    // Cổng TRÊN bị ẩn khi có dải trời — cổng treo giữa cảnh xa trông như lơ lửng.
    this.drawFloorGfx(floorTop, hasSky);

    // ---- DECOR: chỉ thay bản vẽ code khi có ít nhất 1 prop. Thiếu vài ô thì
    // vẽ những ô có (mảng decor giữ nguyên vị trí 9 ô ⇒ prop nào vào đúng chỗ đó).
    if (art.decor.some((k) => k !== null)) {
      for (const o of this.decor) o.destroy();
      this.decor = [];
      this.drawDecorArt(art);
    }

    // ---- NHÀ: vẽ lại theo % máu HIỆN TẠI (ảnh có thể về khi Nhà đã mất máu).
    this.drawCore(this.lastPct);
  }

  /**
   * LƯỚI VÂN SÀN + VIỀN + VÒNG TRÒN TRUNG TÂM + CỔNG QUÁI.
   *
   * Tách thành hàm vì phải vẽ LẠI khi biết có dải trời hay không (`floorTop`):
   * lưới vẽ đè lên cảnh làng quê ban đêm thì hỏng cả hai.
   */
  private drawFloorGfx(floorTop: number, hideTopGate: boolean): void {
    const g = this.floorGfx;
    const h = ARENA.h - floorTop;
    g.clear();

    // vân sàn — chỉ trong VÙNG SÀN
    g.lineStyle(1, 0x000000, 0.06);
    for (let i = 0; i <= 26; i++) {
      const y = floorTop + (i / 26) * h;
      g.lineBetween(0, y, ARENA.w, y);
    }
    for (let i = 0; i <= 40; i++) {
      const x = (i / 40) * ARENA.w;
      g.lineBetween(x, floorTop, x, ARENA.h);
    }
    // viền sân — bao quanh CẢ sân (khung ngoài vẫn đẹp khi có dải trời)
    g.lineStyle(10, 0x2a1f1a, 0.55);
    g.strokeRect(0, 0, ARENA.w, ARENA.h);

    // vòng tròn trung tâm
    g.lineStyle(3, 0xf5d9a8, 0.16);
    g.strokeCircle(ARENA.coreX, ARENA.coreY, 132);
    g.strokeCircle(ARENA.coreX, ARENA.coreY, 108);
    g.lineStyle(2, 0xf5d9a8, 0.1);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      g.lineBetween(
        ARENA.coreX + Math.cos(a) * 108,
        ARENA.coreY + Math.sin(a) * 108,
        ARENA.coreX + Math.cos(a) * 132,
        ARENA.coreY + Math.sin(a) * 132,
      );
    }

    this.drawGates(g, hideTopGate);
  }

  private drawGates(g: Phaser.GameObjects.Graphics, hideTopGate = false): void {
    const gates: Array<[number, number, number]> = [
      [-4, ARENA.h / 2, 0],
      [ARENA.w + 4, ARENA.h / 2, Math.PI],
      [ARENA.w / 2, -4, Math.PI / 2],
      [ARENA.w / 2, ARENA.h + 4, -Math.PI / 2],
    ];
    for (const [x, y, rot] of gates) {
      // cổng TRÊN nằm giữa dải trời (nếu có) ⇒ bỏ, tránh cảnh "cổng lơ lửng trên trời"
      if (hideTopGate && rot === Math.PI / 2) continue;
      g.save();
      g.translateCanvas(x, y);
      g.rotateCanvas(rot);
      g.fillStyle(0x1c1917, 0.75);
      g.fillRect(0, -48, 26, 96);
      g.lineStyle(3, 0x000000, 0.4);
      g.strokeRect(0, -48, 26, 96);
      g.fillStyle(0xdc2626, 0.35);
      g.fillRect(4, -38, 6, 76);
      g.restore();
    }
  }

  /**
   * DECOR bằng ảnh. Scale tính theo CHIỀU CAO mong muốn (bảng DECOR_SLOTS) và bị
   * chặn thêm bởi bề ngang tối đa ⇒ không prop nào cao quá ~140px.
   *
   * Depth: -70 ở mép TRÊN (xa) → -60 ở mép DƯỚI (gần). Chênh 10 đơn vị này tạo
   * cảm giác chiều sâu, và quan trọng hơn: luôn < 0 nên nhân vật luôn đi TRƯỚC decor.
   */
  private drawDecorArt(art: BattlefieldArt): void {
    for (const slot of DECOR_SLOTS) {
      const key = art.decor[slot.prop];
      if (!key) continue;
      const src = this.scene.textures.get(key).getSourceImage() as { width: number; height: number };
      if (!src.width || !src.height) continue;
      const scale = Math.min(slot.h / src.height, DECOR_MAX_W / src.width);
      const img = this.scene.add
        .image(slot.x, slot.y, key)
        .setOrigin(0.5, 1) // neo ĐÁY: prop "đứng" trên mặt sân, không lơ lửng
        .setScale(scale)
        .setAlpha(0.96)
        .setDepth(-70 + (slot.y / ARENA.h) * 10);
      if (slot.flip) img.setFlipX(true); // prop bên phải lật vào trong cho cân
      this.decorImgs.push(img);
    }
  }

  private drawDecor(): void {
    const g = this.scene.add.graphics().setDepth(-70);
    // bụi tre / cây cối ở 4 góc
    const spots: Array<[number, number, number]> = [
      [70, 80, 1], [930, 90, -1], [80, 520, 1], [920, 520, -1],
    ];
    for (const [x, y, dir] of spots) {
      g.fillStyle(0x1f3d2b, 0.8);
      g.fillEllipse(x, y, 90, 120);
      g.fillStyle(0x2d5a3d, 0.7);
      g.fillEllipse(x + 14 * dir, y - 18, 60, 86);
      g.fillStyle(0x3f2a1d, 0.6);
      g.fillRect(x - 4, y + 20, 8, 40);
    }
    // đèn lồng
    for (const [x, y] of [[180, 40], [820, 40]] as Array<[number, number]>) {
      g.fillStyle(0x7f1d1d, 0.9);
      g.fillEllipse(x, y, 18, 24);
      g.lineStyle(2, 0xfbbf24, 0.6);
      g.strokeEllipse(x, y, 18, 24);
      g.lineStyle(1, 0xfbbf24, 0.35);
      g.lineBetween(x, 0, x, y - 12);
    }
    this.decor.push(g);
  }

  /** Vẽ lại Nhà theo % máu còn lại. */
  private drawCore(hpPct: number): void {
    this.lastPct = hpPct; // nhớ lại: ảnh AI về muộn sẽ vẽ đúng trạng thái hiện tại
    const g = this.coreGfx;
    g.clear();
    const { coreX: x, coreY: y } = ARENA;

    // bệ đá — LUÔN vẽ bằng Graphics, kể cả khi có ảnh Nhà.
    // Vì sao: ảnh Nhà do AI vẽ không kèm mặt đất, đứng trơ sẽ như lơ lửng.
    // Ảnh Nhà (depth -3) nằm TRÊN Graphics (-4) nên nửa trước của bệ vẫn lộ ra.
    g.fillStyle(0x3f3f46, 0.9);
    g.fillEllipse(x, y + 34, 150, 40);
    g.lineStyle(3, 0x27272a, 0.8);
    g.strokeEllipse(x, y + 34, 150, 40);

    // CÓ ẢNH NHÀ ⇒ ảnh thay thân + cửa + mái + chóp vàng. Graphics chỉ còn bệ đá.
    if (this.art && this.art.house.some((c) => c !== null)) {
      this.showHouse(hpPct);
      return;
    }

    // ---- BẢN VẼ CODE (mặc định khi chưa có ảnh / ảnh 404) ----
    // thân nhà
    const wall = hpPct > 0.5 ? 0xd6c4a8 : hpPct > 0.25 ? 0xe0b072 : 0xe08b6b;
    g.fillStyle(wall, 1);
    g.fillRect(x - 40, y - 14, 80, 50);
    g.lineStyle(3, 0x2a1f1a, 0.9);
    g.strokeRect(x - 40, y - 14, 80, 50);
    // cửa
    g.fillStyle(0x4a2c1a, 1);
    g.fillRect(x - 12, y + 8, 24, 28);
    // mái
    g.fillStyle(0xb91c1c, 1);
    g.fillTriangle(x - 58, y - 14, x + 58, y - 14, x, y - 52);
    g.lineStyle(3, 0x2a1f1a, 0.9);
    g.strokeTriangle(x - 58, y - 14, x + 58, y - 14, x, y - 52);
    g.fillStyle(0xfbbf24, 1);
    g.fillCircle(x, y - 56, 5);
  }

  /**
   * Vẽ ẢNH Nhà theo ngưỡng máu: >0.5 nguyên vẹn · >0.25 hư nhẹ · còn lại hư nặng.
   *
   * Neo ĐÁY-GIỮA tại (coreX, coreY + 40): đáy tường trong bản vẽ code ở y+36 và
   * bệ đá ở y+34, nên +40 là chỗ Nhà "đặt" lên bệ. Nhờ neo đáy mà dù ảnh AI cao
   * thấp khác nhau thì chân Nhà vẫn luôn chạm bệ, không bị hẫng.
   *
   * KHÔNG che thanh máu: đáy ảnh ở y+40, thanh máu ở y+74 ⇒ luôn cách 34px.
   * Depth ảnh = -3 < 60 của thanh máu (điều kiện bắt buộc đã nêu ở `applyArt`).
   */
  private showHouse(hpPct: number): void {
    const cells = this.art!.house;
    let idx = hpPct > 0.5 ? 0 : hpPct > 0.25 ? 1 : 2;
    // Ô trạng thái mong muốn có thể TRỐNG (agy chỉ vẽ 1-2 ô) ⇒ lùi về ô đầu có ảnh
    let key = cells[idx]?.key ?? null;
    if (!key) {
      const fallback = cells.findIndex((c) => c !== null);
      if (fallback < 0) return;
      idx = fallback;
      key = cells[fallback]!.key;
    }

    if (!this.houseImg) {
      this.houseImg = this.scene.add.image(ARENA.coreX, ARENA.coreY + 40, key).setOrigin(0.5, 1).setDepth(-3);
    } else if (this.houseImg.texture.key !== key) {
      this.houseImg.setTexture(key);
      this.houseState = -1; // texture mới ⇒ phải tính lại scale
    }
    // Tính scale 1 lần cho mỗi trạng thái (ảnh 3 ô có thể cao thấp khác nhau).
    // Chiều cao mục tiêu 124px, bề ngang chặn 180px (bệ đá rộng 150px).
    if (this.houseState !== idx) {
      const src = this.scene.textures.get(key).getSourceImage() as { width: number; height: number };
      if (!src.width || !src.height) return;
      this.houseImg.setScale(Math.min(124 / src.height, 180 / src.width));
      this.houseState = idx;
    }
    // TINT ĐỎ khi máu thấp — giữ đúng tín hiệu "Nhà sắp vỡ" của bản vẽ code
    // (bản code đổi màu tường; ảnh thì không đổi màu được nên dùng tint).
    // `drawCore` chạy mỗi frame nên chỉ setTint khi màu THỰC SỰ đổi.
    const tint = hpPct > 0.5 ? 0xffffff : hpPct > 0.25 ? 0xffd9b8 : 0xff9c7a;
    if (this.houseTint !== tint) {
      this.houseImg.setTint(tint);
      this.houseTint = tint;
    }
  }

  update(core: Core): void {
    const pct = Math.max(0, core.hp / core.maxHp);
    this.coreBar.width = Math.max(0, 216 * pct);
    this.coreBar.fillColor = pct > 0.5 ? 0x22c55e : pct > 0.25 ? 0xf59e0b : 0xef4444;
    const shieldPct = core.shield > 0 ? Math.min(1, core.shield / core.maxHp) : 0;
    this.coreShieldBar.width = 216 * shieldPct;
    this.glow.setAlpha(0.5 + Math.sin(this.scene.time.now / 500) * 0.12);
    this.drawCore(pct);
  }
}
